import React from "react";
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { DEFAULT_BRAND_THEME } from "../themeTokens";

export default function PageStateCard({
  title,
  description,
  tone = "neutral",
  loading = false,
  actionLabel = "",
  onAction,
  brandTheme = DEFAULT_BRAND_THEME,
}) {
  const c = brandTheme;
  const isError = tone === "error";

  return (
    <Card
      role={isError ? "alert" : loading ? "status" : undefined}
      aria-live={isError ? "assertive" : loading ? "polite" : undefined}
      aria-busy={loading || undefined}
      elevation={0}
      sx={{
        borderRadius: { xs: "12px", sm: "14px" },
        border: "1px solid",
        borderColor: isError
          ? "rgba(211,47,47,0.18)"
          : "rgba(124,45,18,0.10)",
        bgcolor: c.surface,
        background: `linear-gradient(145deg, ${c.surface} 0%, ${c.background} 100%)`,
        boxShadow: "0 16px 42px rgba(33,20,12,0.09)",
      }}
    >
      <CardContent sx={{ p: { xs: 2.75, sm: 3.25 }, textAlign: "center" }}>
        <Stack spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: "8px",
              bgcolor: isError ? "rgba(211,47,47,0.10)" : "rgba(255,247,237,0.88)",
              color: isError ? "error.main" : c.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: isError ? "rgba(211,47,47,0.18)" : "rgba(124,45,18,0.10)",
              boxShadow: "0 12px 28px rgba(33,20,12,0.08)",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isError ? (
              <ReportProblemOutlinedIcon />
            ) : (
              <InfoOutlinedIcon />
            )}
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 950,
                color: isError ? "error.main" : c.text,
                letterSpacing: 0,
              }}
            >
              {title}
            </Typography>

            {description ? (
              <Typography
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  lineHeight: 1.55,
                  maxWidth: 520,
                }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>

          {actionLabel && onAction ? (
            <Button
              variant="outlined"
              onClick={onAction}
              sx={{
                minHeight: 44,
                borderRadius: "10px",
                fontWeight: 900,
                textTransform: "none",
                color: c.text,
                borderColor: "rgba(124,45,18,0.16)",
                bgcolor: "rgba(255,255,255,0.52)",
                "&:hover": {
                  bgcolor: "rgba(255,247,237,0.92)",
                  borderColor: "rgba(124,45,18,0.28)",
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": {
                  outline: `3px solid ${c.primary}`,
                  outlineOffset: 2,
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                  "&:hover": {
                    transform: "none",
                  },
                },
              }}
            >
              {actionLabel}
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
